import { io, type Socket } from 'socket.io-client';
import { useUIStore } from '../stores/ui-store';
import { API_ORIGIN } from './api-base';

const TOKEN_KEY = 'tc-auth-token';

let socket: Socket | null = null;

/**
 * Connect to the API WebSocket server with auto-reconnect.
 * Uses the stored JWT for authentication.
 *
 * Socket.IO handles reconnection internally (exponential backoff,
 * infinite retries). We just wire status into the UI store.
 */
export function connectWebSocket(): Socket {
  if (socket?.connected) return socket;

  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) throw new Error('Cannot connect WebSocket: no auth token');

  socket = io(API_ORIGIN || window.location.origin, {
    path: '/api/socket.io',
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    transports: ['websocket'],
  });

  const ui = useUIStore.getState();

  socket.on('connect', () => ui.setWebSocketConnected(true));
  socket.on('disconnect', () => ui.setWebSocketConnected(false));
  socket.on('connect_error', () => ui.setWebSocketConnected(false));

  return socket;
}

export function disconnectWebSocket() {
  socket?.disconnect();
  socket = null;
  useUIStore.getState().setWebSocketConnected(false);
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Set up browser online/offline listeners.
 * Updates the UI store and reconnects the socket on reconnection.
 */
export function setupNetworkListeners() {
  if (typeof window === 'undefined') return;

  const update = () => useUIStore.getState().setOnlineStatus(navigator.onLine);

  window.addEventListener('online', () => {
    update();
    // Socket.IO will auto-reconnect, but force it if disconnected
    const s = getSocket();
    if (s && !s.connected) s.connect();
  });
  window.addEventListener('offline', update);
}
